import { useCallback, useMemo } from 'react'
import { useCopyAdGroupMutation, useCopyPositionMutation } from '../../__generated__/graphql'
import { useCompanyDetailsContext } from './useCompanyDetailsContext'
import { useNavigate } from 'react-router-dom'
import { AdGroupRouteBuilder, PositionRouteBuilder } from '../../pages/routeParams'
import { useSnackbarManager } from '../providers/snackbarContext'
import { useAppTranslations } from './useAppTranslations'
import { useSidebarContext } from '../providers/sidebarContext'

export const useDuplicateAdGroupOrPosition = (id: string, name: string): [() => Promise<void>, boolean] => {
  const t = useAppTranslations()
  const {
    company: { type: companyType, slug },
  } = useCompanyDetailsContext()
  const isPosition = companyType === 'Publisher'
  const navigate = useNavigate()
  const [, setSnackbar] = useSnackbarManager()
  const { reloadItems } = useSidebarContext()
  const [copyPosition, { loading: loadingCopyPosition }] = useCopyPositionMutation()
  const [copyAdGroup, { loading: loadingCopyAdGroup }] = useCopyAdGroupMutation()

  const loading = useMemo(() => loadingCopyPosition || loadingCopyAdGroup, [loadingCopyPosition, loadingCopyAdGroup])

  const duplicatePosition = useCallback(async () => {
    try {
      const { data } = await copyPosition({
        variables: {
          positionId: id,
          name: `${name} (copy)`,
        },
      })
      if (data?.copyPosition) {
        reloadItems()
        navigate(PositionRouteBuilder(slug, data?.copyPosition))
        setSnackbar({ message: t('commons.saved'), severity: 'success' })
      }
    } catch (error) {
      setSnackbar({ message: t('commons.errorOccurred'), severity: 'error' })
      console.error('Error duplicating position: ', error)
    }
  }, [id, name, slug])

  const duplicateAdgroup = useCallback(async () => {
    try {
      const { data } = await copyAdGroup({
        variables: {
          adGroupId: id,
          name: `${name} (copy)`,
          isActive: false,
        },
      })
      if (data?.copyAdGroup) {
        reloadItems()
        navigate(AdGroupRouteBuilder(slug, data?.copyAdGroup))
        setSnackbar({ message: t('commons.saved'), severity: 'success' })
      }
    } catch (error) {
      setSnackbar({ message: t('commons.errorOccurred'), severity: 'error' })
      console.error('Error duplicating adgroup: ', error)
    }
  }, [id, name, slug])

  const duplicate = useMemo(() => (isPosition ? duplicatePosition : duplicateAdgroup), [isPosition])

  return [duplicate, loading]
}
